'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { RefreshCw, Bookmark, Copy, Heart, Check } from 'lucide-react';
import type { Message } from './ChatScreen';

interface Props {
  messages: Message[];
  isTyping: boolean;
  onAction: (type: 'comfort' | 'understand' | 'solve') => void;
  showActions: boolean;
  scrollRef?: React.RefObject<HTMLDivElement>;
  onRegenerate?: (messageId: string) => void;
  onRemember?: (messageId: string, content: string) => void;
}

const ACTION_BUTTONS = [
  { type: 'comfort' as const, emoji: '💜', label: 'Comfort me' },
  { type: 'understand' as const, emoji: '🪞', label: 'Help me understand' },
  { type: 'solve' as const, emoji: '✨', label: 'Help me solve it' },
];

function ClingyAvatar() {
  return (
    <div className="w-7 h-7 rounded-full lavender-gradient flex items-center justify-center flex-shrink-0 self-end mb-1 shadow-sm">
      <span className="text-white text-[10px] font-extrabold tracking-tight">C</span>
    </div>
  );
}

function renderContent(content: string) {
  return content
    .split(/\n{2,}/)
    .flatMap((block) => block.split('\n'))
    .filter((line) => line.trim() !== '')
    .map((line, i) => (
      <p key={i} className="leading-relaxed">
        {line}
      </p>
    ));
}

export default function MessageList({
  messages,
  isTyping,
  onAction,
  showActions,
  scrollRef,
  onRegenerate,
  onRemember,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [helpful, setHelpful] = useState<Record<string, boolean>>({});
  const [remembered, setRemembered] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const el = scrollRef?.current;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isTyping, scrollRef]);

  const handleCopy = useCallback((msgId: string, content: string) => {
    navigator.clipboard.writeText(content).catch(() => {});
    setCopied((prev) => ({ ...prev, [msgId]: true }));
    setTimeout(() => setCopied((prev) => ({ ...prev, [msgId]: false })), 2000);
  }, []);

  const handleRemember = useCallback(
    (msgId: string, content: string) => {
      setRemembered((prev) => ({ ...prev, [msgId]: true }));
      onRemember?.(msgId, content);
    },
    [onRemember]
  );

  return (
    <div className="flex flex-col gap-5 max-w-2xl mx-auto pb-2">
      {messages.map((msg, idx) => {
        const isAI = msg.role === 'ai';
        const isLastAI = isAI && idx === messages.findLastIndex((m) => m.role === 'ai');
        const isHelpful = helpful[msg.id];
        const isRemembered = remembered[msg.id];
        const isCopied = copied[msg.id];

        return (
          <div
            key={msg.id}
            className={`flex message-in ${isAI ? 'items-end gap-2' : 'justify-end'}`}
            style={{ animationDelay: `${Math.min(idx * 15, 120)}ms` }}
          >
            {/* AI avatar */}
            {isAI && <ClingyAvatar />}

            <div className={`flex flex-col gap-1 ${isAI ? 'items-start' : 'items-end'} ${isAI ? 'flex-1 min-w-0' : ''}`}>
              {/* Bubble */}
              {isAI ? (
                <div className="chat-bubble-ai">
                  <div className="flex flex-col gap-2 text-[15px] leading-[1.6] text-[#4A3F38]">
                    {renderContent(msg.content)}
                  </div>
                </div>
              ) : (
                <div className="chat-bubble-user">
                  <div className="flex flex-col gap-1.5 text-[15px] leading-[1.6] text-white">
                    {renderContent(msg.content)}
                  </div>
                  {/* Timestamp + double tick inside bubble */}
                  <div className="flex items-center justify-end gap-1 mt-1.5">
                    <span className="text-[11px] text-white/70 leading-none">{msg.timestamp}</span>
                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
                      <path d="M1 5L4 8L9 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 5L9 8L14 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              )}

              {/* Timestamp — only for AI messages */}
              {isAI && <span className="text-[10px] text-muted-foreground/60 px-1">{msg.timestamp}</span>}

              {/* AI message actions: ♡ Helpful · Save · Copy */}
              {isAI && msg.content.trim() && (
                <div className="flex items-center gap-0.5 mt-0.5 px-0.5">
                  <button
                    onClick={() => setHelpful((prev) => ({ ...prev, [msg.id]: !prev[msg.id] }))}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 active:scale-90 ${
                      isHelpful
                        ? 'text-rose-500 bg-rose-50' :'text-muted-foreground hover:text-rose-400 hover:bg-rose-50/60'
                    }`}
                    title="Mark as helpful"
                  >
                    <Heart size={11} className={isHelpful ? 'fill-rose-500' : ''} />
                    <span>Helpful</span>
                  </button>
                  <span className="text-border text-[10px] select-none">·</span>
                  <button
                    onClick={() => handleRemember(msg.id, msg.content)}
                    disabled={isRemembered}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 active:scale-90 ${
                      isRemembered
                        ? 'text-primary bg-primary/10 cursor-default' :'text-muted-foreground hover:text-primary hover:bg-primary/8'
                    }`}
                    title="Save this"
                  >
                    <Bookmark size={11} className={isRemembered ? 'fill-primary' : ''} />
                    <span>{isRemembered ? 'Saved' : 'Save'}</span>
                  </button>
                  <span className="text-border text-[10px] select-none">·</span>
                  <button
                    onClick={() => handleCopy(msg.id, msg.content)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 active:scale-90 ${
                      isCopied
                        ? 'text-emerald-600 bg-emerald-50' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    title="Copy message"
                  >
                    {isCopied ? <Check size={11} /> : <Copy size={11} />}
                    <span>{isCopied ? 'Copied' : 'Copy'}</span>
                  </button>
                  {/* Regenerate on last AI message */}
                  {isLastAI && onRegenerate && (
                    <>
                      <span className="text-border text-[10px] select-none">·</span>
                      <button
                        onClick={() => onRegenerate(msg.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-muted-foreground hover:text-primary hover:bg-primary/8 transition-all duration-150 active:scale-90"
                        title="Regenerate response"
                      >
                        <RefreshCw size={11} />
                        <span>Retry</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Typing indicator */}
      {isTyping && (
        <div className="flex items-end gap-2 message-in">
          <ClingyAvatar />
          <div className="flex flex-col gap-1 items-start">
            <div className="chat-bubble-ai flex items-center gap-1.5 py-3 px-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50 inline-block typing-dot" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50 inline-block typing-dot" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50 inline-block typing-dot" />
            </div>
            <span className="text-[10px] text-muted-foreground/60 px-1">Clingy is thinking…</span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {showActions && (
        <div className="flex flex-col gap-2 pl-9 fade-in">
          <p className="text-[11px] text-muted-foreground font-medium">Do you want me to…</p>
          <div className="flex flex-wrap gap-2">
            {ACTION_BUTTONS.map((btn) => (
              <button
                key={`action-${btn.type}`}
                onClick={() => onAction(btn.type)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-2xl border border-primary/25 bg-primary/5 text-primary text-xs font-semibold transition-all duration-200 hover:bg-primary/10 active:scale-95"
              >
                <span>{btn.emoji}</span>
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={bottomRef} className="h-1" />
    </div>
  );
}